/**
 * canvas-engine/renderer/elements/index.js
 *
 * OWN per-type element renderer registry for the Canvas Engine.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 * It only aggregates its own sibling element components.
 *
 * Exposes:
 *   - ELEMENT_RENDERERS — map of `{ [elementType]: Component }`
 *   - getElementRenderer(type) — dispatch helper used by the CanvasRenderer
 *     (Task 6.2) to resolve the component for a given element type.
 *
 * Every component shares the inner-content vs positioning CONTRACT: it renders
 * ONLY its inner content (filling width:100%/height:100% of its box) and the
 * parent CanvasRenderer owns the absolute positioning frame. DOM output is
 * identical across builder and reader modes. (Req 4.1, 4.2, 4.3, 20.4–20.7)
 */

import TextElement from './TextElement';
import ImageElement from './ImageElement';
import ButtonElement from './ButtonElement';
import CardElement from './CardElement';
import GridElement from './GridElement';
import ImageGridElement from './ImageGridElement';
import HtmlElement from './HtmlElement';
import LogoElement from './LogoElement';
import IconElement from './IconElement';
import NavElement from './NavElement';
import SocialElement from './SocialElement';
import DataBoundElement from './DataBoundElement';

/**
 * Map of element type → its own presentational renderer component. Keyed by the
 * own `ELEMENT_TYPES` tokens from `model/schema.js`.
 */
export const ELEMENT_RENDERERS = Object.freeze({
  text: TextElement,
  image: ImageElement,
  button: ButtonElement,
  card: CardElement,
  grid: GridElement,
  imageGrid: ImageGridElement,
  html: HtmlElement,
  logo: LogoElement,
  icon: IconElement,
  nav: NavElement,
  social: SocialElement,
  dataBound: DataBoundElement,
});

/**
 * Resolve the renderer component for an element type. Returns `null` for
 * unknown/missing types so the CanvasRenderer can skip or paint a fallback.
 *
 * @param {string} type one of ELEMENT_TYPES
 * @returns {(Function|null)} the React component, or null when unknown
 */
export function getElementRenderer(type) {
  if (typeof type !== 'string') return null;
  return Object.prototype.hasOwnProperty.call(ELEMENT_RENDERERS, type) ? ELEMENT_RENDERERS[type] : null;
}

export {
  TextElement,
  ImageElement,
  ButtonElement,
  CardElement,
  GridElement,
  ImageGridElement,
  HtmlElement,
  LogoElement,
  IconElement,
  NavElement,
  SocialElement,
  DataBoundElement,
};
